import React from 'react';
import { Link } from 'react-router-dom';

const ProfileCard = ({ profile }) => {
  const { user, bio, japaneseLevel, socialLinks, progress } = profile;

  const getLevelColor = (level) => {
    switch (level) {
      case 'beginner': return 'bg-green-100 text-green-700';
      case 'elementary': return 'bg-blue-100 text-blue-700';
      case 'intermediate': return 'bg-yellow-100 text-yellow-700';
      case 'advanced': return 'bg-purple-100 text-purple-700';
      case 'native': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const roleStyles = {
    admin: 'ring-4 ring-red-400 shadow-red-200',
    teacher: 'ring-4 ring-blue-400 shadow-blue-200',
    student: 'ring-2 ring-aqua',
  };

  const badge = user?.role === 'admin' ? 'Admin' : user?.role === 'teacher' ? 'Teacher' : 'Student';
  const avatarClass = roleStyles[user?.role || 'student'];

  return (
    <Link to={`/profiles/${user?._id}`} className="block">
      <div className="card hover:scale-[1.02] transition-transform duration-300 relative overflow-hidden">
      {/* Role badge */}
      <div className="absolute top-3 right-3 text-xs px-2 py-1 rounded-full bg-ocean text-white shadow">
        {badge}
      </div>

      {/* Header with Avatar */}
      <div className="flex items-center gap-4 mb-4">
        {user?.avatar ? (
          <img src={user.avatar} alt={user?.name} className={`w-16 h-16 rounded-full object-cover ${avatarClass}`} />
        ) : (
          <div className={`w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold shadow-lg bg-gradient-to-r from-aqua to-teal ${avatarClass}`}>
            {user?.name?.charAt(0).toUpperCase()}
          </div>
        )}
        <div className="flex-1">
          <h3 className="text-xl font-semibold text-ocean dark:text-sand">{user?.name}</h3>
          <p className="text-sm text-gray-500 dark:text-sand/70">{user?.email}</p>
        </div>
      </div>

      {/* Japanese Level Badge */}
      <div className="mb-3">
        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getLevelColor(japaneseLevel)}`}>
          {japaneseLevel?.charAt(0).toUpperCase() + japaneseLevel?.slice(1)}
        </span>
      </div>

      {/* Bio */}
      {bio && <p className="text-gray-600 dark:text-sand/80 text-sm mb-4 line-clamp-3">{bio}</p>}

      {/* Progress Stats */}
      <div className="grid grid-cols-2 gap-3 mb-4 p-3 bg-ivory dark:bg-steel rounded-lg">
        <div className="text-center">
          <p className="text-2xl font-bold text-teal">{progress?.lessonsCompleted || 0}</p>
          <p className="text-xs text-gray-500 dark:text-sand/70">Lessons</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-ocean">{progress?.currentStreak || 0}</p>
          <p className="text-xs text-gray-500 dark:text-sand/70">Day Streak 🔥</p>
        </div>
      </div>

      {/* Social Links */}
      <div className="flex gap-2 justify-center border-t border-gray-200 dark:border-steel pt-4">
        {socialLinks?.facebook && (
          <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-aqua hover:bg-teal rounded-full flex items-center justify-center text-white transition-colors dark:bg-khaki dark:hover:bg-sand dark:text-night" title="Facebook">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
          </a>
        )}
        {socialLinks?.whatsapp && (
          <a href={`https://wa.me/${socialLinks.whatsapp}`} target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-aqua hover:bg-teal rounded-full flex items-center justify-center text-white transition-colors dark:bg-khaki dark:hover:bg-sand dark:text-night" title="WhatsApp">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          </a>
        )}
        {socialLinks?.instagram && (
          <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-aqua hover:bg-teal rounded-full flex items-center justify-center text-white transition-colors dark:bg-khaki dark:hover:bg-sand dark:text-night" title="Instagram">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
          </a>
        )}
        {socialLinks?.twitter && (
          <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-aqua hover:bg-teal rounded-full flex items-center justify-center text-white transition-colors dark:bg-khaki dark:hover:bg-sand dark:text-night" title="Twitter">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
          </a>
        )}
      </div>
      </div>
    </Link>
  );
};

export default ProfileCard;
